'use client';

import { useEffect, useRef, useState } from 'react';

// FIX-36: extracted from Navbar.tsx — the mobile drawer's open/close state,
// focus trap, Escape/outside-click handling, and the "main content is inert
// while the drawer is open" accessibility behavior. Pulled out verbatim so
// Navbar.tsx isn't carrying this much imperative DOM logic inline; behavior
// is unchanged from the original inline effect.
export function useNavDrawer() {
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const prevActiveRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    prevActiveRef.current = document.activeElement;
    const drawer = drawerRef.current;
    const focusable = drawer?.querySelectorAll<HTMLElement>("a, button, [tabindex]:not([tabindex='-1'])");
    focusable?.[0]?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key === 'Tab') {
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function onClickOutside(e: MouseEvent) {
      const el = drawerRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOutside);

    // Premium pass (2026-08-04): lock the page behind the sheet. Without
    // this, flicking inside the drawer once its list hits the end scrolls
    // the page underneath it - the single most common "cheap-feeling"
    // mobile-menu bug. Position is preserved and restored on close so the
    // page doesn't jump back to the top when the menu is dismissed.
    const scrollY = window.scrollY;
    const body = document.body;
    const prevBodyStyle = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    // mark main content as aria-hidden for screen readers while popup is open
    const main = document.getElementById('content');
    if (main) {
      main.setAttribute('aria-hidden', 'true');
      // also make the main content non-interactive so it cannot overlap or accept clicks
      (main as HTMLElement).style.pointerEvents = 'none';
      (main as HTMLElement).style.userSelect = 'none';
      (main as HTMLElement).style.touchAction = 'none';
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOutside);
      body.style.position = prevBodyStyle.position;
      body.style.top = prevBodyStyle.top;
      body.style.width = prevBodyStyle.width;
      body.style.overflow = prevBodyStyle.overflow;
      window.scrollTo(0, scrollY);
      if (main) {
        main.removeAttribute('aria-hidden');
        (main as HTMLElement).style.pointerEvents = '';
        (main as HTMLElement).style.userSelect = '';
        (main as HTMLElement).style.touchAction = '';
      }
      try {
        (prevActiveRef.current as HTMLElement)?.focus();
      } catch {
        // ignore
      }
    };
  }, [open]);

  return { open, setOpen, drawerRef };
}
